import { useState } from 'react'
import type { ReactNode } from 'react'
import styled from '@emotion/styled'
import {
  faExclamationTriangle,
  faTimes
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  brighten,
  hoverAmount,
  hoverTransition
} from 'src/system/ui/theme/hover'

// The dismissal is intentionally temporary: the user can close the box for a
// day, but it comes back so a still-relevant warning keeps getting seen.
const dismissDurationMs = 24 * 60 * 60 * 1000

function isDismissed(dismissKey: string): boolean {
  try {
    const raw = globalThis.localStorage.getItem(dismissKey)
    if (!raw) return false
    const dismissedAt = Number(raw)
    if (!Number.isFinite(dismissedAt)) return false
    // Reject future timestamps (corruption / clock skew) so a bad value can't
    // keep the warning hidden past the intended window.
    const elapsed = Date.now() - dismissedAt
    return elapsed >= 0 && elapsed < dismissDurationMs
  } catch {
    return false
  }
}

export function DismissableWarning(props: {
  dismissKey: string
  title: ReactNode
  dismissLabel: string
  children: ReactNode
}) {
  const [dismissed, setDismissed] = useState(() =>
    isDismissed(props.dismissKey)
  )

  if (dismissed) return null

  const dismiss = () => {
    try {
      globalThis.localStorage.setItem(props.dismissKey, String(Date.now()))
    } catch {
      // localStorage unavailable (e.g. blocked): hide it for this view. Without
      // persistence it will reappear on a full remount, which is acceptable.
    }
    setDismissed(true)
  }

  return (
    <Box>
      <Header>
        <Title>
          <FontAwesomeIcon icon={faExclamationTriangle} /> {props.title}
        </Title>
        <CloseButton
          type='button'
          onClick={dismiss}
          aria-label={props.dismissLabel}
        >
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
      </Header>
      <Description>{props.children}</Description>
    </Box>
  )
}

const Box = styled.div`
  margin-top: ${(props) => props.theme.d.spacing[2]};
  margin-bottom: ${(props) => props.theme.d.spacing[4]};
  padding: ${(props) =>
    `${props.theme.d.spacing[2]} ${props.theme.d.spacing[4]}`};
  border: 1px solid ${(props) => props.theme.d.colors.warning};
  border-radius: 4px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${(props) => props.theme.d.spacing[2]};
`

const Title = styled.h3`
  color: ${(props) => props.theme.d.colors.warning};
  font-weight: bold;
  font-size: 14px;
  margin: 0;
`

const CloseButton = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  line-height: 1;
  font-size: 14px;
  cursor: pointer;
  color: ${(props) => props.theme.d.colors.grey};
  transition: color ${hoverTransition};

  &:hover {
    color: ${(props) => brighten(props.theme.d.colors.grey, hoverAmount.text)};
  }
`

const Description = styled.p`
  margin: ${(props) => `${props.theme.d.spacing[2]} 0 0`};
  font-size: 13px;
`
